import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import type { NavigateFunction } from 'react-router'
import { snakeCase, camelCase } from 'lodash'
import { Form, Row, CloseButton, Col, Button, ButtonGroup, ButtonToolbar, Container, Stack } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ThunkAction, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { selectCommon, toSnake, Checkbox, DataList, ButtonModals, ErrorModal } from '../common'
import type { AlertState, DataListColumn } from '../common'
import { useAuth } from '../auth'
import type { CurrentUser } from '../auth'
import { fetchMedia, selectMedia, fetchSettings, selectSetting } from '../media'
import type { Media, Setting } from '../media'
import { fetchPages, copyPage, createPage, modifyPage, deletePage } from './pageAPI'
import { selectPageItem } from './pageSelectors'
import {
  selectColumn,
  selectPage,
  selectId,
  unselectId,
  openModal,
  closeModal,
  setSearchCondition,
  dismissError
} from './pageActions'
import type { Page, PageSearchCondition } from './types'
import { selectUser, fetchUsers } from '../user'
import type { User } from '../user'
import { PageUpsertForm, PageCopyForm, PageDeleteForm, PageSearchForm, getPageMarginRelatedLists } from '../page'
import { isLayoutViewableStatus, isLayoutEditableStatus } from '../article'
import type { StatusValue } from '../common'
import moment from 'moment'
moment.locale('ja')

interface EnumValues {
  statuses: Record<string, string>
  columnSettings: Record<string, string>
  pageTypes: Record<string, string>
}

type CreatePageValues = {
  mediaId: number | string
  settingId: number | string
  assigneeId?: number
  pageType: string
  issueDate: string
  issueNumber: number | string
  pageNumber: number | string
  pageRevision: number | string
  pageMarginRegion: string
  pageMarginOtherSelections: number[]
  columnSetting: string
  numberOfCharactersPerRow: number | string
  numberOfRows: number | string
  numberOfColumns: number | string
  bottomAdNumberOfColumns: number | string
  nonFoldingLayout: boolean
  loadedBinaryFile?: { blob: Blob; name: string }
}

type EditPageValues = {
  mediaId: number | string
  settingId: number | string
  assigneeId: number | string
  pageType: string
  issueDate: string
  issueNumber: number | string
  pageNumber: number | string
  pageRevision: number | string
  pageMarginRegion: string
  pageMarginOtherSelections: number[]
  columnSetting: string
  numberOfCharactersPerRow: number | string
  numberOfRows: number | string
  numberOfColumns: number | string
  bottomAdNumberOfColumns: number | string
  nonFoldingLayout: boolean
  deleteFile: boolean
  layoutableType: string
  layoutableId: number
  loadedBinaryFile?: { blob: Blob; name: string }
  lockVersion: unknown
}

type CreateFormSettingParams = {
  handleCreateSubmit: (values: CreatePageValues) => void
  fetching: boolean
  enumValues: unknown
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>
  closeModal: () => { type: string }
  mediaList?: Media[]
  settingList?: Setting[]
  userList?: User[]
  currentUser?: CurrentUser | null
  alert: AlertState | null
}

export const getHandleCreatePageSubmit =
  (
    dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
    navigate: NavigateFunction,
    createPageAPI: typeof createPage
  ) =>
  ({
    mediaId,
    settingId,
    assigneeId,
    pageType,
    issueDate,
    issueNumber,
    pageNumber,
    pageRevision,
    pageMarginRegion,
    pageMarginOtherSelections,
    columnSetting,
    numberOfCharactersPerRow,
    numberOfRows,
    numberOfColumns,
    bottomAdNumberOfColumns,
    nonFoldingLayout,
    loadedBinaryFile
  }: CreatePageValues) => {
    const formData = new window.FormData()
    const pageMarginOtherSelectionsWithIds = pageMarginOtherSelections?.map((id) => ({ id })) ?? []
    const newPage = {
      mediaId,
      assigneeId,
      pageBaseSettingId: settingId,
      pageType: snakeCase(pageType),
      issueDate,
      issueNumber,
      pageNumber,
      pageRevision,
      pageMarginRegion,
      pageMarginOtherSelections: pageMarginOtherSelectionsWithIds,
      columnSetting: snakeCase(columnSetting),
      numberOfCharactersPerRow,
      numberOfRows,
      numberOfColumns,
      bottomAdNumberOfColumns,
      nonFoldingLayout
    }
    formData.append('page', JSON.stringify(toSnake(newPage)))
    if (loadedBinaryFile !== undefined) {
      const { blob, name } = loadedBinaryFile
      formData.append('file', blob, name)
    }
    dispatch(createPageAPI({ navigate, formData }))
  }

// Accepts both pageAPI.modifyPage and articleAPI.modifyPage
type ModifyPageAPI = (params: {
  layoutableType: string
  layoutableId: number
  formData: FormData
}) => ThunkAction<unknown, unknown, unknown, UnknownAction>

export const getHandleEditPageSubmit =
  (dispatch: ThunkDispatch<unknown, unknown, UnknownAction>, modifyPageAPI: ModifyPageAPI) =>
  ({
    mediaId,
    settingId,
    assigneeId,
    pageType,
    issueDate,
    issueNumber,
    pageNumber,
    pageRevision,
    pageMarginRegion,
    pageMarginOtherSelections,
    columnSetting,
    numberOfCharactersPerRow,
    numberOfRows,
    numberOfColumns,
    bottomAdNumberOfColumns,
    nonFoldingLayout,
    deleteFile,
    layoutableType,
    layoutableId,
    loadedBinaryFile,
    lockVersion
  }: EditPageValues) => {
    const formData = new window.FormData()
    const pageMarginOtherSelectionsWithIds = pageMarginOtherSelections?.map((id) => ({ id })) ?? []
    const newPage = {
      mediaId,
      pageBaseSettingId: settingId,
      assigneeId,
      pageType: snakeCase(pageType),
      issueDate,
      issueNumber,
      pageNumber,
      pageRevision,
      pageMarginRegion,
      pageMarginOtherSelections: pageMarginOtherSelectionsWithIds,
      columnSetting: snakeCase(columnSetting),
      numberOfCharactersPerRow,
      numberOfRows,
      numberOfColumns,
      bottomAdNumberOfColumns,
      nonFoldingLayout,
      lockVersion
    }
    formData.append('page', JSON.stringify(toSnake(newPage)))
    formData.append('lock_version', String(lockVersion))
    if (deleteFile) {
      formData.append('delete_file', 'true')
    } else if (loadedBinaryFile !== undefined) {
      const { blob, name } = loadedBinaryFile
      formData.append('file', blob, name)
    }
    dispatch(modifyPageAPI({ layoutableType, layoutableId, formData }))
  }

export const getCreateFormSetting = ({
  handleCreateSubmit,
  fetching,
  enumValues,
  dispatch,
  closeModal,
  mediaList,
  settingList,
  userList,
  currentUser,
  alert
}: CreateFormSettingParams) => {
  const mediaId = mediaList && mediaList.length > 0 ? mediaList[0].id : ''
  const selectedSettingList =
    typeof mediaId === 'number' ? (settingList?.filter((s) => s.media.id === mediaId) ?? []) : []
  const settingId = selectedSettingList.length > 0 ? selectedSettingList[0].id : ''
  const selectedSetting = selectedSettingList?.[0]
  const selectedColumnSetting = selectedSetting?.columnSetting
  const { pageMarginRegionList } = getPageMarginRelatedLists(selectedSetting)
  const today = moment(new Date()).format('YYYY-MM-DD')
  return {
    initialValues: {
      mediaId,
      settingId,
      assigneeId: currentUser?.id,
      selectedSettingList,
      columnSetting: selectedColumnSetting?.typeOf || '',
      numberOfCharactersPerRow: selectedColumnSetting?.numberOfCharactersPerRow || '',
      numberOfRows: selectedColumnSetting?.numberOfRows || '',
      numberOfColumns: selectedColumnSetting?.numberOfColumns || '',
      pageType: 'left_page_spread',
      issueDate: today,
      issueNumber: 1,
      pageNumber: 1,
      pageRevision: 1,
      pageMarginRegion: pageMarginRegionList?.[0] || '',
      pageMarginOtherSelections: [],
      bottomAdNumberOfColumns: 0,
      nonFoldingLayout: false
    },
    handleSubmit: handleCreateSubmit,
    validationSchema: PageUpsertForm.schema,
    Component: PageUpsertForm,
    props: {
      title: '紙面新規作成',
      fetching,
      enumValues,
      onClickCancel: () => dispatch(closeModal()),
      submitButtonText: '作成する',
      mediaList,
      settingList,
      userList,
      currentUser,
      alert
    }
  }
}

interface SearchConditionsProps {
  searchCondition: PageSearchCondition
  mediaList?: Media[]
}

const SearchConditions = ({ searchCondition, mediaList }: SearchConditionsProps) => {
  const isEmpty = Object.values(searchCondition).every((x) => x === '')
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>()
  if (isEmpty) {
    return
  }
  const mediaIdInt = parseInt(searchCondition?.mediaId)
  const mediaName = mediaList?.find((m) => m.id === mediaIdInt)?.name
  const issueDate = searchCondition?.issueDate ? moment(searchCondition?.issueDate).format('LL') : undefined
  const clearMediaNameSearchCondition = () => {
    dispatch(setSearchCondition({ name: 'mediaId', value: '' }))
  }
  const clearIssueDateSearchCondition = () => {
    dispatch(setSearchCondition({ name: 'issueDate', value: '' }))
  }
  const alertStyle = {
    color: '#0c5460',
    backgroundColor: '#d1ecf1'
  }
  const searchConditionClassNames = 'border border-info rounded p-1 my-0'
  return (
    <Row sm='auto'>
      <Col>
        <Stack
          direction='horizontal'
          gap={2}
          className='border border-secondary rounded d-flex align-items-center my-2 p-2'
        >
          <div>絞り込み条件</div>
          {mediaName && (
            <div style={alertStyle} className={searchConditionClassNames}>
              <CloseButton onClick={clearMediaNameSearchCondition} />
              媒体名: {mediaName}
            </div>
          )}
          {issueDate && (
            <div style={alertStyle} className={searchConditionClassNames}>
              <CloseButton onClick={clearIssueDateSearchCondition} />
              発行日: {issueDate}
            </div>
          )}
        </Stack>
      </Col>
    </Row>
  )
}

export const PageList = () => {
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>()
  const {
    data: pageList,
    currentPage,
    totalPages,
    sort,
    selectedIds,
    searchCondition,
    openedModal,
    fetching: pageFetching,
    errors,
    alert
  } = useSelector(selectPageItem)
  const { currentUser } = useAuth()
  const { enumValues } = useSelector(selectCommon) as unknown as { enumValues: EnumValues }
  const { data: mediaList, fetching: mediaFetching } = useSelector(selectMedia)
  const { data: userList, fetching: userFetching } = useSelector(selectUser)
  const { data: settingList, fetching: settingFetching } = useSelector(selectSetting)
  const navigate = useNavigate()
  useEffect(() => {
    dispatch(fetchPages({ page: currentPage, sort, search: searchCondition }))
  }, [dispatch, sort, currentPage, searchCondition])
  const [fetching, setFetching] = useState(true)
  const [fetched, setFetched] = useState(false)
  useEffect(() => {
    if (pageFetching || mediaFetching || settingFetching || userFetching) {
      setFetching(true)
    } else {
      setFetching(false)
    }
  }, [pageFetching, mediaFetching, settingFetching, userFetching])

  useEffect(() => {
    if (openedModal !== null && !fetched) {
      dispatch(fetchMedia({ all: true }))
      dispatch(fetchSettings({ all: true }))
      dispatch(fetchUsers({}))
      setFetched(true)
    }
  }, [dispatch, fetched, openedModal])

  const mediaIdsInSettings = new Set(settingList?.map((s) => s.media.id) || [])
  const filteredMediaList = mediaList?.filter((m) => mediaIdsInSettings.has(m.id))

  const columns: DataListColumn[] = [
    {
      id: 'checkboxColumn',
      meta: { cellProps: { style: { width: '40px', textAlign: 'center' } } },
      cell: (props) => (
        <Checkbox
          checked={selectedIds.includes(parseInt(props.row.original.id))}
          value={props.row.original.id}
          onChange={(value: string, checked: boolean) => dispatch((checked ? selectId : unselectId)(parseInt(value)))}
          disabled={fetching}
        />
      )
    },
    { name: '名称', accessorKey: 'name', sortKey: 'name' },
    {
      name: 'ステータス',
      accessorKey: 'status',
      cell: (info: any) => enumValues.statuses[camelCase(info.getValue())],
      sortKey: 'status'
    },
    {
      name: '字数/行数/段数',
      accessorKey: 'layoutRelated',
      cell: (props) => {
        const { numberOfCharactersPerRow, numberOfRows, numberOfColumns } = props.row.original as Page
        return `${numberOfCharactersPerRow}/${numberOfRows}/${numberOfColumns}`
      }
    },
    {
      name: '担当者',
      accessorKey: 'assignee',
      cell: (info: any) => info.getValue().name
    },
    {
      name: '最終更新者',
      accessorKey: 'updatedBy',
      cell: (info: any) => info.getValue().name
    },
    {
      name: '更新日時',
      accessorKey: 'updatedAt',
      cell: (info: any) => moment(info.getValue()).format('LLL'),
      sortKey: 'updatedAt'
    },
    {
      id: 'actionColumn',
      name: '記事詳細',
      cell: (props) => (
        <Form className='d-flex'>
          <ButtonToolbar>
            <ButtonGroup size='sm'>
              <Button className='list-button' onClick={() => navigate(`/pages/${props.row.original.id}/articles`)}>
                <FontAwesomeIcon icon='list' />
                出稿表
              </Button>
              <Button
                className='layout-button'
                onClick={() => navigate(`/pages/${props.row.original.id}/layout`)}
                disabled={!isLayoutViewableStatus(props.row.original.status as StatusValue)}
              >
                <FontAwesomeIcon icon='table' />
                紙面
              </Button>
            </ButtonGroup>
          </ButtonToolbar>
        </Form>
      )
    }
  ]

  const handleColumnClick = (columnName: string) => () => {
    dispatch(selectColumn(columnName))
  }

  const handlePaginationClick = (page: number) => dispatch(selectPage(page))

  const handleCreateSubmit = getHandleCreatePageSubmit(dispatch, navigate, createPage)

  const handleEditSubmit = getHandleEditPageSubmit(dispatch, modifyPage)

  const handleCopySubmit = ({ draftNumber, copyLayoutInfo }: { draftNumber: number; copyLayoutInfo: boolean }) => {
    if (selectedIds.length < 1) {
      return
    }

    const id = selectedIds[0]
    dispatch(copyPage({ id, draftNumber, copyLayoutInfo, navigate }))
  }

  const handleDeleteSubmit = () => {
    if (selectedIds.length < 1) {
      return
    }

    const id = selectedIds[0]
    dispatch(deletePage({ id }))
  }

  const handleSearchSubmit = ({ mediaId, issueDate }: { mediaId: string; issueDate: string }) => {
    const newSearchConditions = [
      { name: 'mediaId', newValue: mediaId },
      { name: 'issueDate', newValue: issueDate }
    ]
    newSearchConditions.forEach(({ name, newValue }) => {
      dispatch(setSearchCondition({ name, value: newValue }))
    })
    dispatch(closeModal())
  }

  const singleSelectedPage = selectedIds.length === 1 ? pageList.find((item) => item.id === selectedIds[0]) : null
  const isSelectedPageReadable =
    singleSelectedPage &&
    (currentUser?.role === 'group_administrator' || currentUser?.id === singleSelectedPage.assignee.id)
  const isSelectedPageWritable =
    isSelectedPageReadable && isLayoutEditableStatus(singleSelectedPage.status as StatusValue)
  const createFormSetting = getCreateFormSetting({
    handleCreateSubmit,
    fetching,
    enumValues,
    dispatch,
    closeModal,
    mediaList: filteredMediaList,
    settingList,
    userList,
    currentUser,
    alert
  })

  const toolSettings = [
    {
      name: 'create',
      button: {
        handleClick: () => {
          dispatch(openModal('create'))
        },
        disabled: fetching || currentUser?.role === 'global_administrator',
        icon: 'plus',
        text: '新規'
      },
      form: createFormSetting
    },
    {
      name: 'edit',
      button: {
        handleClick: () => {
          dispatch(openModal('edit'))
        },
        disabled: fetching || !isSelectedPageReadable,
        icon: 'pencil',
        text: '編集'
      },
      form: {
        initialValues: () => {
          if (selectedIds.length < 1) {
            return {}
          }
          const pageId = selectedIds[0]
          const selectedPage = pageList.find((item) => item.id === pageId)
          if (!selectedPage) {
            return {}
          }
          const issueDate = moment(selectedPage.issueDate).format('YYYY-MM-DD')
          const mediaId = selectedPage.media.id
          const selectedSettingList = settingList?.filter((s) => s.media.id === mediaId) ?? []
          return {
            layoutableType: 'Page',
            layoutableId: pageId,
            mediaId,
            settingId: selectedPage.setting.id,
            assigneeId: selectedPage.assignee.id,
            selectedSettingList,
            ...selectedPage,
            issueDate
          }
        },
        handleSubmit: handleEditSubmit,
        validationSchema: PageUpsertForm.schema,
        Component: PageUpsertForm,
        props: {
          title: '紙面編集',
          fetching,
          enumValues,
          onClickCancel: () => dispatch(closeModal()),
          submitButtonText: '変更する',
          mediaList: filteredMediaList,
          settingList,
          userList,
          currentUser,
          alert
        }
      }
    },
    {
      name: 'copy',
      button: {
        handleClick: () => {
          dispatch(openModal('copy'))
        },
        disabled: fetching || !isSelectedPageReadable,
        icon: 'copy',
        text: '複製'
      },
      form: {
        initialValues: () => {
          const selectedPage = pageList.find((item) => item.id === selectedIds[0])
          if (!selectedPage) {
            return {}
          }
          const largestDraftNumber =
            selectedPage.pageDrafts.map((p) => p.draftNumber).sort((a, b) => b - a)?.[0] ?? selectedPage.draftNumber
          return {
            name: selectedPage.name,
            draftNumber: largestDraftNumber + 1,
            copyLayoutInfo: false
          }
        },
        handleSubmit: handleCopySubmit,
        validationSchema: PageCopyForm.schema,
        Component: PageCopyForm,
        props: {
          title: '紙面複製',
          fetching,
          onClickCancel: () => dispatch(closeModal()),
          submitButtonText: '複製する',
          alert
        }
      }
    },
    {
      name: 'delete',
      button: {
        handleClick: () => {
          dispatch(openModal('delete'))
        },
        disabled: fetching || !isSelectedPageWritable,
        icon: 'trash',
        variant: 'outline-danger',
        text: '削除'
      },
      form: {
        initialValues: () => {
          const selectedPage = pageList.find((item) => item.id === selectedIds[0])
          if (!selectedPage) {
            return {}
          }

          return {
            ...selectedPage
          }
        },
        handleSubmit: handleDeleteSubmit,
        Component: PageDeleteForm,
        props: {
          title: '紙面削除',
          fetching,
          onClickCancel: () => dispatch(closeModal()),
          submitButtonText: '削除する'
        }
      }
    },
    {
      name: 'search',
      button: {
        handleClick: () => {
          dispatch(openModal('search'))
        },
        disabled: fetching,
        icon: 'sliders-h',
        text: '絞り込む'
      },
      form: {
        initialValues: {
          issueDate: searchCondition?.issueDate || '',
          mediaId: searchCondition?.mediaId || ''
        },
        handleSubmit: handleSearchSubmit,
        validationSchema: PageSearchForm.schema,
        Component: PageSearchForm,
        props: {
          title: '紙面絞り込み',
          fetching,
          onClickCancel: () => dispatch(closeModal()),
          submitButtonText: '絞り込む',
          mediaList,
          alert
        }
      }
    }
  ]

  return (
    <Container fluid>
      <ErrorModal errors={errors} onClose={(errorKey: string) => dispatch(dismissError(errorKey))} />
      <ButtonModals settings={toolSettings} openedModal={openedModal} notReady={!fetched || fetching} />
      <SearchConditions searchCondition={searchCondition} mediaList={mediaList} />
      <DataList
        columns={columns}
        data={pageList}
        currentPage={currentPage}
        totalPages={totalPages}
        onColumnClick={handleColumnClick}
        sort={sort}
        onPaginationClick={handlePaginationClick}
      />
    </Container>
  )
}
